import React, { useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link, useParams, Navigate } from "react-router-dom";
import { Minus, Plus, ArrowUpRight } from "lucide-react";
import { useProducts, useProduct } from "../context/ProductsContext";
import ProductReviews from "../components/ProductReviews";
import ProductJsonLd from "../components/ProductJsonLd";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const currency = (n) => `$${n.toFixed(2)}`;

export default function ProductPage() {
  const { id } = useParams();
  const { product, loading } = useProduct(id);
  const { products } = useProducts();
  const { add } = useCart();
  const [qty, setQty] = useState(1);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 120]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[color:var(--sr-bg)] flex items-center justify-center">
        <p className="label-eyebrow text-[color:var(--sr-muted)] flex items-center gap-3">
          <span className="w-2 h-2 rounded-full bg-[color:var(--sr-accent)] animate-pulse" />
          Loading
        </p>
      </div>
    );
  }
  if (!product) return <Navigate to="/shop" replace />;

  const others = products.filter((p) => p.id !== product.id).slice(0, 3);

  return (
    <div data-testid={`product-page-${product.id}`} className="bg-[color:var(--sr-bg)]">
      <ProductJsonLd product={product} />
      {/* Hero */}
      <section className="relative pt-28 md:pt-32">
        <div className="sr-container">
          <div className="grid grid-cols-12 gap-6 md:gap-10">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="col-span-12 lg:col-span-7 relative"
            >
              <motion.div
                style={{ y: heroY }}
                className="relative aspect-[4/5] w-full overflow-hidden bg-[color:var(--sr-secondary)]"
              >
                <div
                  className="absolute inset-0 opacity-90 mix-blend-multiply"
                  style={{
                    background: `radial-gradient(55% 55% at 50% 55%, ${product.accent}33 0%, transparent 70%)`,
                  }}
                />
                <img
                  src={product.image}
                  alt={product.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <span className="absolute top-6 left-6 label-eyebrow text-[color:var(--sr-primary)]/70">
                  N°{product.number} — {product.name}
                </span>
              </motion.div>
            </motion.div>

            <div className="col-span-12 lg:col-span-5 flex flex-col justify-center lg:pl-6">
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15 }}
                className="label-eyebrow text-[color:var(--sr-primary)] mb-6 flex items-center gap-3"
              >
                <span className="w-8 h-px bg-[color:var(--sr-accent)]" /> The bottle
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.2 }}
                className="font-serif text-6xl md:text-8xl leading-[0.9] tracking-tight text-[color:var(--sr-primary)]"
              >
                {product.name.split(" ").map((w, i) => (
                  <span key={i} className="block">
                    {i === product.name.split(" ").length - 1 ? (
                      <em className="italic font-light text-[color:var(--sr-accent)]">
                        {w}.
                      </em>
                    ) : (
                      w + " "
                    )}
                  </span>
                ))}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.35 }}
                className="mt-8 text-[color:var(--sr-text)]/75 leading-relaxed max-w-md"
              >
                {product.long_description}
              </motion.p>

              <div className="mt-10 flex flex-wrap items-center gap-3">
                {product.notes.map((n) => (
                  <span
                    key={n}
                    className="label-eyebrow px-3 py-1.5 border hairline text-[color:var(--sr-primary)]"
                  >
                    {n}
                  </span>
                ))}
              </div>

              <div className="mt-12 flex items-center justify-between border-t hairline-light pt-8">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    data-testid="product-dec"
                    className="p-3 min-h-[44px] min-w-[44px] flex items-center justify-center border hairline hover:border-[color:var(--sr-primary)] transition-colors"
                    aria-label="Decrease"
                  >
                    <Minus size={14} strokeWidth={1.5} />
                  </button>
                  <span
                    className="w-8 text-center tabular-nums text-[color:var(--sr-primary)] font-serif text-xl"
                    data-testid="product-qty"
                  >
                    {qty}
                  </span>
                  <button
                    onClick={() => setQty((q) => q + 1)}
                    data-testid="product-inc"
                    className="p-3 min-h-[44px] min-w-[44px] flex items-center justify-center border hairline hover:border-[color:var(--sr-primary)] transition-colors"
                    aria-label="Increase"
                  >
                    <Plus size={14} strokeWidth={1.5} />
                  </button>
                </div>
                <p className="font-serif text-3xl text-[color:var(--sr-primary)] tabular-nums">
                  {currency(product.price * qty)}
                </p>
              </div>

              <button
                onClick={() => {
                  add(
                    {
                      id: product.id,
                      lookup_key: product.lookup_key,
                      name: product.name,
                      price: product.price,
                      image: product.image,
                    },
                    qty
                  );
                  toast.success(`${product.name} added to bag`);
                }}
                data-testid="add-to-cart-btn"
                className="mt-8 group inline-flex items-center justify-between w-full px-8 py-5 bg-[color:var(--sr-primary)] text-[color:var(--sr-secondary)] label-eyebrow hover:bg-[color:var(--sr-primary-deep)] transition-colors"
              >
                <span>Add to bag</span>
                <span className="w-6 h-px bg-current transition-all group-hover:w-10" />
              </button>
              <p className="mt-4 text-xs text-[color:var(--sr-muted)]">
                Ships within 48 hours · Cold-packed · Recyclable glass
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial split */}
      <section className="py-24 md:py-40">
        <div className="sr-container grid grid-cols-12 gap-6 md:gap-12 items-center">
          <div className="col-span-12 lg:col-span-5">
            <p className="label-eyebrow text-[color:var(--sr-primary)] mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-[color:var(--sr-accent)]" /> A slow ritual
            </p>
            <h2 className="font-serif text-4xl md:text-6xl leading-tight tracking-tight text-[color:var(--sr-primary)]">
              For the pause,
              <br />
              <em className="italic font-light text-[color:var(--sr-accent)]">
                and what follows.
              </em>
            </h2>
            <p className="mt-8 text-[color:var(--sr-text)]/75 leading-relaxed max-w-md">
              {product.description}
            </p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1 }}
            className="col-span-12 lg:col-span-6 lg:col-start-7"
          >
            <div className="relative aspect-[5/6] w-full overflow-hidden bg-[color:var(--sr-secondary)]">
              <img
                src={product.lifestyle}
                alt={`${product.name} lifestyle`}
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Other bottles */}
      <section className="pb-24 md:pb-40 border-t hairline-light pt-24 md:pt-32">
        <div className="sr-container">
          <div className="flex items-end justify-between mb-12">
            <h3 className="font-serif text-3xl md:text-5xl text-[color:var(--sr-primary)] tracking-tight">
              Others in the collection
            </h3>
            <Link
              to="/shop"
              className="label-eyebrow inline-flex items-center gap-2 text-[color:var(--sr-primary)] hover:opacity-70"
              data-testid="view-all-link"
            >
              View all <ArrowUpRight size={14} strokeWidth={1.25} />
            </Link>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {others.map((p) => (
              <li key={p.id} className="group">
                <Link to={`/product/${p.id}`}>
                  <div className="relative aspect-[4/5] overflow-hidden bg-[color:var(--sr-secondary)]">
                    <div
                      className="absolute inset-0 opacity-70 mix-blend-multiply"
                      style={{
                        background: `radial-gradient(55% 55% at 50% 55%, ${p.accent}22 0%, transparent 70%)`,
                      }}
                    />
                    <img
                      src={p.image}
                      alt={p.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="mt-4 flex items-baseline justify-between">
                    <p className="font-serif text-xl text-[color:var(--sr-primary)] group-hover:text-[color:var(--sr-accent)] transition-colors">
                      {p.name}
                    </p>
                    <p className="text-sm text-[color:var(--sr-muted)] tabular-nums">
                      {currency(p.price)}
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
      <ProductReviews productId={product.id} productName={product.name} />
    </div>
  );
}
